import { Database, Home, Image, LayoutGrid, QrCode } from 'lucide-react';
import React from 'react';
import { Link, useLocation } from 'react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; // Import useTranslation

// Removed nav item definition from here

/**
 * BottomNav component provides navigation icons at the bottom of the screen for mobile devices
 * - Shows key navigation options as icons
 * - Highlights the currently active section
 * - Provides quick access to essential app features
 */
const BottomNav: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation(); // Initialize translation hook

  // Navigation items for the bottom bar (Moved inside component)
  const navItems = [
    {
      path: '/',
      label: t('navigation.home', 'Home'),
      icon: <Home size={22} />
    },
    {
      path: '/rooms',
      label: t('navigation.rooms', 'Rooms'),
      icon: <LayoutGrid size={22} />
    },
    {
      path: '/scan',
      label: t('navigation.scan', 'Scan'),
      icon: <QrCode size={22} />
    },
    {
      path: '/exhibits',
      label: t('navigation.exhibits', 'Exhibits'),
      icon: <Image size={22} />
    },
    {
      path: '/tours',
      label: t('navigation.tours', 'Tours'),
      icon: <Database size={22} />
    }
  ];

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white dark:bg-neutral-900 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] dark:shadow-[0_-2px_10px_rgba(0,0,0,0.2)] pb-safe">
      <ul className="flex justify-between max-w-md mx-auto">
        {navItems.map((item) => (
          <li key={item.path} className="flex-1">
            <Link
              to={item.path}
              className={`relative flex flex-col items-center py-3 text-xs transition-colors ${
                isActive(item.path)
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-neutral-500 dark:text-neutral-400'
              }`}
            >
              {/* Special treatment for the scan button */}
              {item.label === 'Scan' ? (
                <motion.div
                  className="bg-blue-600 dark:bg-blue-500 text-white p-3 rounded-full -mt-6 shadow-lg"
                  whileTap={{ scale: 0.9 }}
                >
                  {item.icon}
                </motion.div>
              ) : (
                <>
                  <div className="text-xl">{item.icon}</div>
                  <span className="mt-1 text-[11px]">{item.label}</span>

                  {/* Active indicator - small dot below active item */}
                  {isActive(item.path) && (
                    <motion.div
                      layoutId="activeNavIndicator"
                      className="absolute bottom-1 h-1 w-1 rounded-full bg-blue-600 dark:bg-blue-400"
                      transition={{ type: "spring", duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default BottomNav;
